import type { TenantSummary } from "../api/types";
import type { Translate } from "../i18n";

/** Under this, the end date stops being a footnote and starts being a warning. */
export const WARN_WITHIN_DAYS = 7;

export function daysLeft(expiresAt: string | null): number | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  return Math.ceil(ms / 86_400_000);
}

export interface AccessNotice {
  text: string;
  tone: "normal" | "warning" | "error";
}

/**
 * How long this account has left, said plainly.
 *
 * The customer cannot change any of it themselves, so the only job here is to
 * make "call us" obvious before the phones stop rather than after.
 */
export function accessNotice(tenant: TenantSummary, t: Translate): AccessNotice {
  if (tenant.status === "suspended") {
    return { text: t("account.suspended"), tone: "error" };
  }
  if (!tenant.expires_at) {
    return { text: t("account.unlimited"), tone: "normal" };
  }
  const date = new Date(tenant.expires_at).toLocaleDateString();
  const days = daysLeft(tenant.expires_at) ?? 0;
  if (days < 0) return { text: t("account.ended", { date }), tone: "error" };
  if (days === 0) return { text: t("account.endsToday"), tone: "error" };
  if (days <= WARN_WITHIN_DAYS) {
    return { text: t("account.endsInDays", { n: days }), tone: "warning" };
  }
  return { text: t("account.until", { date }), tone: "normal" };
}

/**
 * What a console we opened from ours wears (B2B §7.1).
 *
 * This tab was handed its session by the admin console and is signed in as one
 * of the customer's own logins. Everything done in it is done as them and
 * lands in their audit trail under their name — so the one thing this screen
 * must not do is look exactly like our own, and the one thing it must always
 * answer is "whose console am I in?".
 */
export function SupportBadge({
  email,
  tenant,
  onEnd,
  t,
}: {
  email: string;
  tenant: string;
  onEnd: () => void;
  t: Translate;
}) {
  return (
    <span className="support-badge" role="status">
      <strong>{t("support.session")}</strong>
      {/* The company first: it is what the click chose, and what a screenshot
          in a support thread has to be able to prove. */}
      <span className="support-who">
        {tenant} · {email}
      </span>
      <button type="button" onClick={onEnd}>
        {t("support.end")}
      </button>
    </span>
  );
}

/** The sidebar's footer: which company this dashboard belongs to, and until when. */
export function AccountBadge({ tenant, t }: { tenant: TenantSummary; t: Translate }) {
  const notice = accessNotice(tenant, t);
  return (
    <div className="account-badge" data-tone={notice.tone}>
      <div className="account-name">{tenant.name}</div>
      <div className="account-access">{notice.text}</div>
    </div>
  );
}
